import { Deserializer } from "jsonapi-serializer";
import { getProfileTypeBySysname, PRESET_TYPES, SUB_TYPES, SYSNAME_SYSTEM, SYSNAME_TEMPLATE_DEFAULT, SYSNAME_TYPE, SYSNAME_USER_DEFINED } from "../constants/preset-constants";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { DevicePreset, DevicePresetWrapper } from "../models/DevicePreset";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { OldPreset } from "../models/OldPreset";
import { PaginationModel } from "../models/Pagination";
import { PresetReaderWrapper } from "../models/PresetReaderWrapper";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

export const getControllerPreset = async (listParameters?: ListQuery): Promise<DevicePresetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsControllerProfiles = 'fields[controllerProfiles]=isDefault,trnName,profileSysname,totalControllers,profileType,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `controllerProfiles?include=site${partialURL}&${fieldsControllerProfiles}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const hardwarePreset: DevicePreset[] = data.map((permission): DevicePreset => {
                    return {
                        id: permission['id'],
                        name: permission['trn-name'],
                        type: getProfileTypeBySysname(permission['profile-sysname']),
                        devices: permission['total-controllers'],
                        site: permission['site']['site-name'],
                        isDefault: permission['is-default']
                    }
                });

                return resolve({ rows: hardwarePreset, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getIODevicePreset = async (listParameters?: ListQuery): Promise<DevicePresetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsIODeviceProfiles = 'fields[moduleProfiles]=isDefault,trnName,profileSysname,totalModules,profileType,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `moduleProfiles?include=site${partialURL}&${fieldsIODeviceProfiles}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const ioModulePreset: DevicePreset[] = data.map((preset): DevicePreset => {
                    return {
                        id: preset['id'],
                        name: preset['trn-name'],
                        type: getProfileTypeBySysname(preset['profile-sysname']),
                        devices: preset['total-modules'],
                        site: preset['site']['site-name'],
                        isDefault: preset['is-default']
                    }
                });
                return resolve({ rows: ioModulePreset, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReaderPresets = async (listParameters?: ListQuery): Promise<DevicePresetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsReaderProfiles = 'fields[readerProfiles]=isDefault,trnName,profileSysname,totalReaders,profileType,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `readerProfiles?include=site${partialURL}&${fieldsReaderProfiles}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const hardwarePreset: DevicePreset[] = data.map((permission): DevicePreset => {
                    return {
                        id: permission['id'],
                        name: permission['trn-name'],
                        type: getProfileTypeBySysname(permission['profile-sysname']),
                        devices: permission['total-readers'],
                        site: permission['site']['site-name'],
                        description: permission['p-desc'] ?? '',
                        isDefault: permission['is-default']
                    }
                });

                return resolve({ rows: hardwarePreset, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReaderPresetsId = async (readerPresetId): Promise<DevicePreset> => {
    try {
        const url = `readerProfiles?include=site,profileType&filter=id==${readerPresetId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const hardwarePreset: DevicePreset = {
                    id: data[0]['id'],
                    name: data[0]['trn-name'],
                    type: getProfileTypeBySysname(data[0]['profile-sysname']),
                    devices: data[0]['total-readers'],
                    site: data[0]['site']['site-name'],
                    siteId: data[0]['site']['id'],
                    profileTypeId: data[0]['profile-type']['id'],
                    pInheritsFrom: data[0]['profile-inherits-from'],
                    inheritsFromId: data[0]['inherits-from-id'],
                    description: data[0]['p-desc'] ?? '',
                    totalDevices: data[0]['total-readers'],
                    totalActions: data[0]['total-actions'],
                    totalParameters: data[0]['total-parameters'],
                    totalInputs: data[0]['total-inputs'],
                    totalDoorModePatterns: data[0]['total-door-mode-patterns'],
                    totalOutputs: data[0]['total-outputs'],
                    isDefault: data[0]['is-default'],
                    pSysnameType: data[0]['p-sysname-type'],
                }

                return resolve(hardwarePreset);
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeReaderPreset = async (readerPreset: DevicePreset, createReaderPreset = true) => {
    try {
        const newRecord = {
            data: {
                type: 'profile',
                id: readerPreset.id,
                attributes: {
                    pAlias: null,
                    pDesc: readerPreset.description,
                    pHidden: 0,
                    trnName: readerPreset.name,
                    pSysname: SYSNAME_USER_DEFINED,
                    pSysnameType: "",
                    inheritsFromId: readerPreset.inheritsFromId
                },
                relationships: {
                    profileType: {
                        data: {
                            type: "profileType",
                            id: "1"
                        }
                    },
                    site: {
                        data: {
                            type: 'site',
                            id: readerPreset.siteId
                        }
                    },
                }
            }
        }

        const url = `profile${createReaderPreset ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createReaderPreset, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({status: response.status,message: genericError});
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createReaderPreset) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newReaderPreset) => {
                        if (error) {
                            return reject({status: response.status, id: newReaderPreset.id, success: false, message: genericError });
                        }
                        return resolve({ id: newReaderPreset.id, success: true });
                    })

                    return
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: readerPreset.id, success: true })

                }

                const json = await response.json();
                return reject({status: response.status, id: readerPreset.id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            reject({status: INTERNAL_SERVER_ERROR, id: readerPreset.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getControllerPresetsId = async (controllerPresetId): Promise<DevicePreset> => {
    try {
        const controllerProfilesFields = 'fields[controllerProfiles]=trnName,profileSysname,site,profileType,inheritsFrom,profileInheritsFrom,pDesc,totalControllers,totalActions,totalParameters,totalInputs,isDefault'
        const siteFields = 'fields[site]=siteName'
        const url = `controllerProfiles/${controllerPresetId}?include=site,profileType,inheritsFrom&${controllerProfilesFields}&${siteFields}`
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const hardwarePreset: DevicePreset = {
                    id: data['id'],
                    name: data['trn-name'],
                    type: getProfileTypeBySysname(data['profile-sysname']),
                    site: data['site']['site-name'],
                    siteId: data['site']['id'],
                    profileTypeId: data['profile-type']['id'],
                    pInheritsFrom: data['profile-inherits-from'],
                    inheritsFromId: data['inherits-from-id'],
                    description: data['p-desc'] ?? '',
                    totalDevices: data['total-controllers'],
                    totalActions: data['total-actions'],
                    totalParameters: data['total-parameters'],
                    totalInputs: data['total-inputs'],
                    isDefault: data['is-default']
                }

                return resolve(hardwarePreset);
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeControllerPreset = async (controllerPreset: DevicePreset, createControllerPreset = true) => {
    try {
        const newRecord = {
            data: {
                type: 'profile',
                id: controllerPreset.id,
                attributes: {
                    pAlias: null,
                    pDesc: controllerPreset.description,
                    pHidden: 0,
                    trnName: controllerPreset.name,
                    pSysname: SYSNAME_USER_DEFINED,
                    pSysnameType: "",
                    inheritsFromId: controllerPreset.inheritsFromId
                },
                relationships: {
                    profileType: {
                        data: {
                            type: "profileType",
                            id: "2"
                        }
                    },
                    site: {
                        data: {
                            type: 'site',
                            id: controllerPreset.siteId
                        }
                    },
                }
            }
        }
        const url = `profile${createControllerPreset ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createControllerPreset, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error))
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createControllerPreset) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newControllerPreset) => {
                        if (error) {
                            return reject({status: response.status, id: newControllerPreset.id, success: false, message: genericError });
                        }
                        return resolve({ id: newControllerPreset.id, success: true });
                    })

                    return;
                }

                if (response.status === 204) {
                    return resolve({ id: controllerPreset.id, success: true })
                }

                const json = await response.json();
                return reject({status: response.status, id: controllerPreset.id, success: false, message: getConstraintViolation(json.errrors[0].detail) });
            }

            return reject({status: INTERNAL_SERVER_ERROR, id: controllerPreset.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getIODevicePresetsId = async (ioDevicePresetId): Promise<DevicePreset> => {
    try {
        const url = `moduleProfiles?include=site,profileType,inheritsFrom&filter=id==${ioDevicePresetId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }
                const hardwarePreset: DevicePreset = {
                    id: data[0]['id'],
                    name: data[0]['trn-name'],
                    type: getProfileTypeBySysname(data[0]['profile-sysname']),
                    devices: data[0]['total-modules'],
                    site: data[0]['site']['site-name'],
                    siteId: data[0]['site']['id'],
                    profileTypeId: data[0]['profile-type']['id'],
                    pInheritsFrom: data[0]['profile-inherits-from']?.toString(),
                    inheritsFromId: data[0]['inherits-from-id'],
                    description: data[0]['p-desc'] ?? '',
                    totalDevices: data[0]['total-modules'],
                    totalActions: data[0]['total-actions'],
                    totalParameters: data[0]['total-parameters'],
                    totalInputs: data[0]['total-inputs'],
                    totalDoorModePatterns: data[0]['total-door-mode-patterns'],
                    totalOutputs: data[0]['total-outputs'],
                    isDefault: data[0]['is-default'],
                    pSysnameType: data[0]['p-sysname-type']
                }

                return resolve(hardwarePreset);
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeIODevicePreset = async (ioDevicePreset: DevicePreset, createIODevicePreset = true) => {
    try {
        const newRecord = {
            data: {
                type: 'profile',
                id: ioDevicePreset.id,
                attributes: {
                    pAlias: null,
                    pDesc: ioDevicePreset.description,
                    pHidden: 0,
                    trnName: ioDevicePreset.name,
                    pSysname: SYSNAME_USER_DEFINED,
                    pSysnameType: SYSNAME_TYPE.IO,
                    inheritsFromId: ioDevicePreset.inheritsFromId
                },
                relationships: {
                    profileType: {
                        data: {
                            type: "profileType",
                            id: "1"
                        }
                    },
                    site: {
                        data: {
                            type: 'site',
                            id: ioDevicePreset.siteId
                        }
                    },
                }
            }
        }

        const url = `profile${createIODevicePreset ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createIODevicePreset, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createIODevicePreset) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newIODevicePreset) => {
                        if (error) {
                            return reject({status: response.status, id: newIODevicePreset.id, success: false, message: genericError });
                        }
                        return resolve({ id: newIODevicePreset.id, success: true });
                    })

                    return;
                }

                if (response.status === 204) {
                    return resolve({ id: ioDevicePreset.id, success: true })
                }

                const json = await response.json();
                return reject({status: response.status, id: ioDevicePreset.id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            return reject({status: INTERNAL_SERVER_ERROR, id: ioDevicePreset.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deletePreset = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `profile/${id}`;
        const profile = {
            data: {
                type: "profile",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, profile)

        return new Promise(async (resolve, reject) => {
            if (response.status === 204) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricDevicePreset = async (listParameters?: ListQuery, pst_id?: string): Promise<DevicePresetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsBioProfiles = 'fields[bioProfiles]=isDefault,trnName,profileSysname,profileType,profileSubType,totalBiometrics,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `bioProfiles?include=site,profileType,profileSubType,inheritsFrom${partialURL}&${fieldsBioProfiles}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                let biometricPreset: DevicePreset[] = data.map((preset): DevicePreset => {
                    return {
                        id: preset['id'],
                        name: preset['trn-name'],
                        type: getProfileTypeBySysname(preset['profile-sysname']),
                        devices: preset['total-biometrics'],
                        site: preset['site']['site-name'],
                        pInheritsFrom: preset['profile-inherits-from']?.toString(),
                        inheritsFromId: preset['inherits-from-id'],
                        totalDevices: preset['total-biometrics'],
                        profileTypeId: data[0]['profile-type']['id'],
                        pSysnameType: data[0]['p-sysname-type'],
                        pAlias: data[0]['p-alias'],
                        isDefault: preset['is-default'],
                        profileSubTypeId: preset['profile-sub-type'] ? preset['profile-sub-type']['id'] : "0",
                    }
                });

                if (pst_id) {
                    biometricPreset = biometricPreset.filter((preset) => preset.profileSubTypeId === pst_id);
                }

                return resolve({ rows: biometricPreset, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricDevicePresetNameWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const broProfilesFields = 'fields[bioProfiles]=trnName,isDefault';
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `bioProfiles?include=site${partialURL}&${broProfilesFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const biometricPreset: Name[] = items.map((preset): Name => {
                    return {
                        value: preset['id'],
                        label: preset['trn-name'],
                        isDefault: preset['is-default']
                    }
                });

                return resolve({ rows: biometricPreset, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricPresetById = async (biometricPresetId): Promise<DevicePreset> => {
    try {
        const bioProfilesFields = 'fields[bioProfiles]=trnName,profileSysname,profileType,profileSubType,totalBiometrics,site,pDesc,pSysnameType,profileInheritsFrom,pAlias,isDefault,totalActions,totalInputs,totalParameters';
        const siteFields = 'fields[site]=siteName';
        const url = `bioProfiles?include=site,profileSubType,profileType,inheritsFrom&filter=id==${biometricPresetId}&${bioProfilesFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const biometricPreset: DevicePreset = {
                    id: data[0]['id'],
                    name: data[0]['trn-name'],
                    type: getProfileTypeBySysname(data[0]['profile-sysname']),
                    devices: data[0]['total-biometrics'],
                    site: data[0]['site']['site-name'],
                    siteId: data[0]['site']['id'],
                    description: data[0]['p-desc'],
                    profileSubTypeId: data[0]['profile-sub-type'] ? data[0]['profile-sub-type']['id'] : "0",
                    pInheritsFrom: data[0]['profile-inherits-from']?.toString(),
                    inheritsFromId: data[0]['inherits-from-id'],
                    totalDevices: data[0]['total-biometrics'],
                    profileTypeId: data[0]['profile-type']['id'],
                    pSysnameType: data[0]['p-sysname-type'],
                    pAlias: data[0]['p-alias'],
                    isDefault: data[0]['is-default'],
                    totalActions: data[0]['total-actions'],
                    totalInputs: data[0]['total-inputs'],
                    totalModules: data[0]['total-modules'],
                    totalDoorModePatterns: data[0]['total-door-mode-patterns'],
                    totalOutputs: data[0]['total-outputs'],
                    totalParameters: data[0]['total-parameters'],
                }

                return resolve(biometricPreset);
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const isOriginal = (id: number) => id === 60 || id === 69;
const buildBioPreset = (bioPreset: DevicePreset, isNew: boolean = false) => ({
    data: {
        type: 'profile',
        id: bioPreset.id,
        attributes: {
            pAlias: bioPreset.pAlias,
            pDesc: bioPreset.description,
            pHidden: 0,
            trnName: bioPreset.name,
            ...(isNew
                ? {
                    pSysname: SYSNAME_USER_DEFINED,
                    pSysnameType: bioPreset.pSysnameType,
                    inheritsFromId: bioPreset.inheritsFromId,
                }
                : {
                    profileSysnameType: bioPreset.pSysnameType,
                    profileInheritsFrom: bioPreset.pInheritsFrom,
                }
            ),
        },
        relationships: {
            profileType: {
                data: {
                    type: "profileType",
                    id: '9'
                }
            },
            ...(isNew && bioPreset.profileSubTypeId && {
                profileSubType: {
                    data: {
                        type: "profileSubType",
                        id: bioPreset.profileSubTypeId
                    }
                }
            }),
            site: {
                data: {
                    type: 'site',
                    id: bioPreset.siteId
                }
            },
        }
    }
});
export const storeBioPreset = async (bioPreset: DevicePreset, createBioPreset = true) => {
    try {
        const isNew = bioPreset.id === undefined || bioPreset.id === 0;
        const recordToSave = buildBioPreset(bioPreset, isNew)

        const url = `profile${createBioPreset ? '' : `/${recordToSave.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createBioPreset, recordToSave).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createBioPreset) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newReaderPreset) => {
                        if (error) {
                            return reject({ status: response.status, id: newReaderPreset.id, success: false, message: genericError });
                        }
                        return resolve({ id: newReaderPreset.id, success: true });
                    })

                    return;
                }

                if (response.status === 204) {
                    return resolve({ id: bioPreset.id, success: true });
                }

                return resolve({ id: recordToSave.data.id, success: true });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: bioPreset.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getLockPresets = async (listParameters?: ListQuery): Promise<DevicePresetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsLockProfiles = 'fields[lockProfiles]=isDefault,trnName,profileSysname,totalReaders,profileType,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `lockProfiles?include=site${partialURL}&${fieldsLockProfiles}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const lockPresets: DevicePreset[] = data.map((lockPreset): DevicePreset => {
                    return {
                        id: lockPreset['id'],
                        name: lockPreset['trn-name'],
                        type: getProfileTypeBySysname(lockPreset['profile-sysname']),
                        devices: lockPreset['total-readers'],
                        site: lockPreset['site']['site-name'],
                        description: lockPreset['p-desc'] ?? '',
                        isDefault: lockPreset['is-default']
                    }
                });

                return resolve({ rows: lockPresets, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getLockPresetsById = async (lockPresetId): Promise<DevicePreset> => {
    try {
        const url = `lockProfiles?include=site,profileType&filter=id==${lockPresetId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                if (data.length > 0) {
                    const lockPreset: DevicePreset = {
                        id: data[0]['id'],
                        name: data[0]['trn-name'],
                        type: getProfileTypeBySysname(data[0]['profile-sysname']),
                        devices: data[0]['total-readers'],
                        site: data[0]['site']['site-name'],
                        siteId: data[0]['site']['id'],
                        profileTypeId: data[0]['profile-type']['id'],
                        pInheritsFrom: data[0]['profile-inherits-from'],
                        inheritsFromId: data[0]['inherits-from-id'],
                        description: data[0]['p-desc'] ?? '',
                        totalDevices: data[0]['total-readers'],
                        totalActions: data[0]['total-actions'],
                        totalParameters: data[0]['total-parameters'],
                        totalInputs: data[0]['total-inputs'],
                        totalDoorModePatterns: data[0]['total-door-mode-patterns'],
                        totalOutputs: data[0]['total-outputs'],
                        isDefault: data[0]['is-default'],
                        pSysnameType: data[0]['p-sysname-type']
                    }
                    return resolve(lockPreset);
                }
                return {}
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeLockPreset = async (lockPreset: DevicePreset, createLockPreset = true) => {
    try {
        const lockProfile = "11"
        const newRecord = {
            data: {
                type: 'profile',
                id: lockPreset.id,
                attributes: {
                    pAlias: null,
                    pDesc: lockPreset.description,
                    pHidden: 0,
                    trnName: lockPreset.name,
                    pSysname: SYSNAME_USER_DEFINED,
                    pSysnameType: "",
                    inheritsFromId: lockPreset.inheritsFromId
                },
                relationships: {
                    profileType: {
                        data: {
                            type: "profileType",
                            id: lockProfile
                        }
                    },
                    site: {
                        data: {
                            type: 'site',
                            id: lockPreset.siteId
                        }
                    },
                }
            }
        }

        const url = `profile${createLockPreset ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createLockPreset, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({status: response.status,message: genericError});
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createLockPreset) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newReaderPreset) => {
                        if (error) {
                            return reject({status: response.status, id: newReaderPreset.id, success: false, message: genericError });
                        }
                        return resolve({ id: newReaderPreset.id, success: true });
                    })

                    return
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: lockPreset.id, success: true })

                }

                const json = await response.json();
                return reject({status: response.status, id: lockPreset.id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            reject({status: INTERNAL_SERVER_ERROR, id: lockPreset.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const profileNamesBuilder = (presetType: string) => {
    switch (presetType) {
        case PRESET_TYPES.CONTROLLER:
            return "controllerProfiles";
        case PRESET_TYPES.READER:
            return "readerProfiles";
        case PRESET_TYPES.IOMODULE:
            return "moduleProfiles";
        case PRESET_TYPES.LOCK:
            return "lockProfiles";
        case PRESET_TYPES.LOCKER:
            return "lockerProfiles";
        case PRESET_TYPES.BIOMETRIC:
            return "bioProfiles"
        default:
            return "controllerProfiles";
    }
};

export const getDeviceProfileNames = async (profileName: string, listParameters?: ListQuery, includeSbType?: boolean): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const profile = profileNamesBuilder(profileName)
        const url = `${profile}?fields[${profile}]=trnName,isDefault${partialURL}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceProfiles: Name[] = items.map((deviceProfile): Name => {
                    return {
                        value: deviceProfile['id'],
                        label: deviceProfile['trn-name'],
                        isDefault: deviceProfile['is-default']
                    };
                });

                return resolve({ rows: deviceProfiles, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildProfileSubTypes = (preset) => preset.map((item: any) => {
    return {
        presetId: item.id,
        presetSubTypeId: item?.relationships?.profileSubType?.data?.id
    }
});

export const getDeviceProfileTypes = async (profileName: string, translate: Translator ,listParameters?: ListQuery, includeSbType?: boolean): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const profile = profileNamesBuilder(profileName)
        const url = `${profile}?fields[${profile}]=trnName,isDefault,profileSubType${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);
        let presetSbTypes: any[] = []

        if (includeSbType) {
            presetSbTypes = buildProfileSubTypes(json.data);  // helps to show the preset  sub type in the profile list
        }

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const deviceProfiles: Name[] = items.map((deviceProfile): Name => {
                    const type = presetSbTypes?.find((item) => item.presetId === deviceProfile['id'])?.presetSubTypeId
                    return {
                        value: deviceProfile['id'],
                        label: getBioProfileLabel(type, deviceProfile['trn-name'], translate),
                        isDefault: deviceProfile['is-default'],
                        subTypeId: type
                    };
                });

                return resolve({ rows: deviceProfiles, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const getBioProfileLabel = (type: string, profileName: string, translator: Translator) => {
    if (type === SUB_TYPES.IDEMIA) {
        return `${profileName} (${translator.byKey(TranslationKeys.idemia)})`;
    }
    else if (type === SUB_TYPES.CONTROL_ID) {
        return `${profileName} (${translator.byKey(TranslationKeys.controlId)})`;
    }
    else if (type === SUB_TYPES.HIK_VISION) {
        return `${profileName} (${translator.byKey(TranslationKeys.hikVision)})`;
    }
    else if (type === SUB_TYPES.HID_AMICO) {
        return `${profileName} (${translator.byKey(TranslationKeys.hidAmico)})`;
    }

    return profileName;
}

const endpointBuilder = (presetType: string) => {
    switch (presetType) {
        case PRESET_TYPES.CONTROLLER:
            return "controller";
        case PRESET_TYPES.READER:
            return "terminal";
        case PRESET_TYPES.IOMODULE:
            return "terminal";
        case PRESET_TYPES.LOCK:
            return "terminal";
        default:
            return "controller";
    }
};

const getFieldType = (presetType: string): string => {
    return presetType === 'CONTROLLER' ? 'controller' : 'terminal';
};

export const getDevicesPerPresetType = async (itemId: string, presetType: string, listParameters?: ListQuery): Promise<PresetReaderWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterProfile = `${filter ? filter.replace('filter=', `filter=profile.id==${itemId};`) : `&filter=profile.id==${itemId}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filterProfile);
        const endpoint = endpointBuilder(presetType);
        const includes = "include=unitType";
        const deviceType = getFieldType(presetType)
        const deviceFields = `fields[${deviceType}]=devName,devFixedAddr,devIpAddress,unitType`;
        const unitTypeFields = "fields[unitType]=trnName";
        const url = `${endpoint}?${includes}${paginationString}&${deviceFields}&${unitTypeFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const presetLock: OldPreset[] = data.map(preset => {
                    return {
                        id: preset['id'],
                        name: preset['dev-name'],
                        fixedAddress: preset['dev-fixed-addr'],
                        ipAddress: preset['dev-ip-address'],
                        type: preset['unit-type']['trn-name'],
                    }
                });

                return resolve({ rows: presetLock, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
    }
};

export const getLockersByPreset = async (itemId: string, presetType: string, listParameters?: ListQuery): Promise<PresetReaderWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterProfile = `${filter ? filter.replace('filter=', `filter=profile.id==${itemId};`) : `&filter=profile.id==${itemId}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filterProfile);
        const url = `locker?include=lockerType${paginationString}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const presetLock: OldPreset[] = data.map(locker => {
                    return {
                        id: locker['id'],
                        name: locker['locker-name'],
                        serialAddress: locker['locker-address'],
                        type: locker['locker-type']['lt-name'],
                    }
                });

                return resolve({ rows: presetLock, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
    }
};

export const getLockerPresets = async (listParameters?: ListQuery): Promise<DevicePresetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `lockerProfiles?include=site${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const lockerPresets: DevicePreset[] = data.map((lockerPreset): DevicePreset => {
                    return {
                        id: lockerPreset['id'],
                        name: lockerPreset['trn-name'],
                        type: getProfileTypeBySysname(lockerPreset['profile-sysname']),
                        devices: lockerPreset['total-members'],
                        site: lockerPreset['site']['site-name'],
                        description: lockerPreset['p-desc'] ?? '',
                        isDefault: lockerPreset['is-default']
                    }
                });

                return resolve({ rows: lockerPresets, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getLockerPresetsById = async (lockPresetId): Promise<DevicePreset> => {
    try {
        const url = `lockerProfiles?include=site,profileType&filter=id==${lockPresetId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                if (data.length > 0) {
                    const lockPreset: DevicePreset = {
                        id: data[0]['id'],
                        name: data[0]['trn-name'],
                        type: getProfileTypeBySysname(data[0]['profile-sysname']),
                        devices: data[0]['total-members'],
                        site: data[0]['site']['site-name'],
                        siteId: data[0]['site']['id'],
                        profileTypeId: data[0]['profile-type']['id'],
                        pInheritsFrom: data[0]['profile-inherits-from'],
                        inheritsFromId: data[0]['inherits-from-id'],
                        description: data[0]['p-desc'] ?? '',
                        totalDevices: data[0]['total-members'],
                        isDefault: data[0]['is-default'],
                        pSysnameType: data[0]['p-sysname-type']
                    }

                    return resolve(lockPreset);
                }

                return {}
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
